import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const inputClass = "bg-[#0D2A52] border-amber-500/20 text-white";
const labelClass = "text-white";
export default function ExperimentalSettings() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = async () => {
    setLoading(true); setError(false);
    try { const { data } = await api.get("/experimental-settings"); setForm(data); }
    catch { setError(true); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const set = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const save = async event => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const { updated_at, ...values } = form;
      const { data } = await api.put("/experimental-settings", { ...values, minimum_age: Number(values.minimum_age) });
      setForm(data); toast.success("Informações da aula experimental atualizadas");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return <Card className="bg-transparent gsx-card-dark p-5 sm:p-6 mb-8">
    <h2 className="text-xl text-white font-semibold">Informações da aula experimental</h2>
    <p className="text-sm text-slate-400 mt-2 mb-5">As alterações serão exibidas na página pública e usadas nas novas solicitações.</p>
    {loading && <p role="status" className="text-slate-400">Carregando informações…</p>}
    {error && <div role="alert" className="text-red-300">Não foi possível carregar as informações.
      <Button variant="outline" onClick={load} className="ml-3">Tentar novamente</Button></div>}
    {!loading && !error && form && <form onSubmit={save} data-testid="experimental-settings-form">
      <fieldset disabled={saving} className="grid sm:grid-cols-2 gap-4">
        {[["title", "Título da página", 150, true], ["badge", "Selo (ex.: Grátis)", 60, false], ["location", "Local / endereço", 300, false]].map(([key, label, maxLength, required]) =>
          <div key={key} className="space-y-1"><Label htmlFor={`lesson-${key}`} className={labelClass}>{label}</Label>
            <Input id={`lesson-${key}`} value={form[key]} maxLength={maxLength} required={required} onChange={e => set(key, e.target.value)} className={inputClass} /></div>)}
        <div className="space-y-1"><Label htmlFor="lesson-day" className={labelClass}>Dia da semana</Label>
          <select id="lesson-day" value={form.day} onChange={e => set("day", e.target.value)} className={`w-full rounded-md border p-2 ${inputClass}`}>
            {["Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado", "Domingo"].map(day => <option key={day}>{day}</option>)}
          </select></div>
        <div className="space-y-1"><Label htmlFor="lesson-time" className={labelClass}>Horário</Label>
          <Input id="lesson-time" type="time" value={form.time} required onChange={e => set("time", e.target.value)} className={inputClass} /></div>
        <div className="space-y-1"><Label htmlFor="lesson-age" className={labelClass}>Idade mínima</Label>
          <Input id="lesson-age" type="number" min="1" max="100" step="1" required value={form.minimum_age} onChange={e => set("minimum_age", e.target.value)} className={inputClass} /></div>
        <div className="sm:col-span-2 space-y-1"><Label htmlFor="lesson-whatsapp" className={labelClass}>WhatsApp de confirmação</Label>
          <Input id="lesson-whatsapp" type="tel" inputMode="numeric" pattern="[1-9][0-9]{9,14}" maxLength={15} value={form.whatsapp} onChange={e => set("whatsapp", e.target.value)} className={inputClass} />
          <p className="text-xs text-slate-400">Inclua país e DDD, somente números (ex.: 5579998852357). Deixe vazio para receber apenas pelo painel.</p></div>
        {[["description", "Descrição da aula"], ["instructions", "Orientações adicionais"]].map(([key, label]) =>
          <div key={key} className="sm:col-span-2 space-y-1"><Label htmlFor={`lesson-${key}`} className={labelClass}>{label}</Label>
            <Textarea id={`lesson-${key}`} rows={3} maxLength={2000} required={key === "description"} value={form[key]} onChange={e => set(key, e.target.value)} className={inputClass} /></div>)}
        <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={form.accepting_requests} onChange={e => set("accepting_requests", e.target.checked)} />Receber novas solicitações
        </label>
        <Button type="submit" className="bg-amber-500 text-[#071A33] hover:bg-amber-400" disabled={saving}>{saving ? "Salvando…" : "Salvar informações"}</Button>
        <a href="/aula-experimental" target="_blank" rel="noreferrer" className="text-sm text-amber-300 self-center">Ver página pública</a>
      </fieldset>
    </form>}
  </Card>;
}
